import React, { useCallback, useState } from 'react';
import { ExpandIcon } from 'lucide-react';
import { lookInside } from '../data/content';
import { pageCaptions } from '../data/images';
import { ImageSlot } from './ImageSlot';
import { Lightbox } from './Lightbox';
import { SectionHeading } from './SectionHeading';
type LookInsideSectionProps = {
  pageImages: string[];
};

/** "See what's inside": real 9:16 pages of the book. Wide cards on mobile (one page at a time), tap to open full size. */
export function LookInsideSection({
  pageImages
}: LookInsideSectionProps) {
  const [active, setActive] = useState<number | null>(null);
  const close = useCallback(() => setActive(null), []);
  const captions = pageCaptions;

  // Hidden until the real pages of the book are loaded in data/images.ts
  if (pageImages.length === 0) return null;
  return <section id="look-inside" aria-labelledby="look-title" className="scroll-mt-4 bg-ink py-12 md:py-20" data-id="element-152" data-mp="3r05zk9z7u3x0">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-153" data-mp="3m2dx4m0y2zek">
        <SectionHeading id="look-title" tone="dark" eyebrow={lookInside.eyebrow} title={lookInside.title} sub={lookInside.sub} data-id="element-154" data-mp="3b4oz6b4zdp05" />
        <p className="mt-3 text-center text-[13px] text-white/75" data-id="element-155" data-mp="3g41kusk48q15">{lookInside.hint}</p>
      </div>

      <ul className="no-scrollbar mx-auto mt-8 flex max-w-page snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-5 px-5 pb-2 md:mt-10 md:gap-4 md:scroll-px-8 md:px-8" aria-label="Sample pages" data-id="element-156" data-mp="3l1l3e4nujoyw">
        {pageImages.map((url, i) => <li key={captions[i] ?? i} className="w-[80%] shrink-0 snap-center sm:w-[46%] md:w-[31%] lg:w-[23%]" data-id="element-157" data-mp="2nu8du1gjv2f" data-mpi={i}>
            <button type="button" onClick={() => setActive(i)} className="group block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sun" aria-label={`Open full size: ${captions[i]}`} data-id="element-158" data-mp="7n6vmfw8r45j">
              <div className="relative aspect-[9/16] overflow-hidden rounded-xl bg-[#14201B] ring-1 ring-white/10 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.8)]" data-id="element-159" data-mp="2eeg2kyncitei">
                <ImageSlot url={url} alt={captions[i]} slotName={`pageImages[${i}]`} fit="contain" data-id="element-160" data-mp="21encz3j4xta4" />
                <span className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-sun text-ink shadow-md transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-161" data-mp="2gbxo2brv8x5b">
                  <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-162" data-mp="2pbawd1497fog" />
                </span>
              </div>
              <p className="mt-3 text-[14px] font-medium text-white" data-id="element-163" data-mp="2kbyeyvw5j0t9">
                <span className="mr-2 font-display font-extrabold text-sun" data-id="element-164" data-mp="2z1mbljte1ban">{String(i + 1).padStart(2, '0')}</span>
                {captions[i]}
              </p>
            </button>
          </li>)}
      </ul>
      <p className="mx-auto mt-4 max-w-page px-5 text-center text-[13px] text-white/70 md:px-8" data-id="element-165" data-mp="381sq990tohbe">{lookInside.note}</p>

      <Lightbox images={pageImages} captions={captions} index={active} onClose={close} onChange={setActive} data-id="element-166" data-mp="342a1g25vrm3a" />
    </section>;
}